import {Fragment, useEffect, useState} from "react";
import {useLocation, useNavigationType} from "react-router-dom";
import {Loader} from "@mantine/core";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {TopBarSlot} from "../chat/TopBarContext";
import {AccessContext, type AccessEntry} from "./AccessContext";
import {useContentParams} from "./router";
import {ChecklistResource} from "./ChecklistResource";
import {FavoriteTopicButton} from "./FavoriteTopicButton";
import {DocumentGeneratorResource} from "./DocumentGeneratorResource";
import {HTMLResource} from "./HTMLResource";
import {NewsResource} from "./NewsResource";
import {NoAccessModal} from "./NoAccessModal";
import {PdfResource} from "./pdf/PdfResource";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import {ResourceType} from "./types";
import {pageFallbackSource, useTopicOpenAnalytics} from "./useTopicOpenAnalytics";
import statusClasses from "./StatusMessage.module.css";
import classes from "./ContentPage.module.css";
import {PanelProvider} from "./panel/PanelContext";
import {SliderPanel} from "./panel/SliderPanel";
import {APP_TITLE} from "./useContentTitle";

/** The anchor a link pointed to; a malformed hash is ignored, never thrown. */
const sectionFromHash = (hash: string) => {
    try {
        return decodeURIComponent(hash.replace(/^#/, "")) || undefined;
    } catch {
        return undefined;
    }
};

export const ContentPage = () => {
    const {
        resourceId,
        dokumentId,
        collectionId,
        segmentId,
        topicType,
        isValidTopicType,
        isPreview,
    } = useContentParams();
    const location = useLocation();
    const navigationType = useNavigationType();

    const isSkipped = !resourceId || (!isPreview && !isValidTopicType);

    // Topic-level access verdict, reported by whichever leaf loaded the topic
    // data (see AccessContext), tagged with the leaf's resourceId. A stale
    // entry from a previous topic simply stops matching, which both clears the
    // gate on navigation and keeps it up across intra-topic segment fetches.
    const [accessEntry, setAccessEntry] = useState<AccessEntry | null>(null);
    const noAccess =
        accessEntry !== null &&
        accessEntry.id === resourceId &&
        !accessEntry.access;
    const {
        currentData: resourceTypeId,
        isError,
        error,
    } = dibAppApi.endpoints.getResourceTypeId.useQuery(
        {topicId: resourceId ?? ""},
        {skip: isSkipped}
    );
    const isPrefetchLoading =
        !isSkipped && !isError && resourceTypeId === undefined;

    // A topic counts as opened once its leaf has shown it to a user with
    // access. PDFs load the document after the access check, so they report
    // the document load separately. Editor previews are never counted. The
    // resource type is always the type of the current topic: it is undefined
    // while the type of a new topic loads.
    const hasAccess =
        accessEntry !== null &&
        accessEntry.id === resourceId &&
        accessEntry.access;
    const pdfDocumentLoaded = useTopicOpenAnalytics({
        topicId: resourceId,
        shown: !isPreview && hasAccess && resourceTypeId !== undefined,
        surface: "page",
        segmentId,
        sectionId: sectionFromHash(location.hash),
        topicType,
        fallbackSource: pageFallbackSource(navigationType),
        waitForDocument: resourceTypeId === ResourceType.pdf,
    });

    // Title is owned by the leaf resource components (they set
    // `<topic name> - DIB` once their data loads). ContentPage covers the
    // windows where no leaf is mounted: keep "DIB" while resolving the resource
    // type / skipped / errored, and reset on leaving any content route.
    useEffect(() => {
        if (isSkipped || isPrefetchLoading || isError) {
            document.title = APP_TITLE;
        }
    }, [isSkipped, isPrefetchLoading, isError]);

    useEffect(() => {
        return () => {
            document.title = APP_TITLE;
        };
    }, []);

    const getContent = () => {
        const notFound = (
            <StatusMessage
                title="Content not found"
                detail="The page you're looking for doesn't exist or the URL is invalid."
            />
        );

        if (isSkipped) {
            return notFound;
        }

        if (isPrefetchLoading) {
            return (
                <div className={statusClasses.statusMessage}>
                    <Loader color="gneis.5" size="md" />
                </div>
            );
        }

        if (isError) {
            return <ResourceError error={error} />;
        }

        // The lookup resolved without a type (empty body / null).
        if (!resourceTypeId) {
            return notFound;
        }

        switch (resourceTypeId) {
            case ResourceType.news:
                return <NewsResource resourceId={resourceId!} />;
            case ResourceType.checklist:
                return (
                    <ChecklistResource
                        resourceId={resourceId!}
                        collectionId={collectionId}
                        dokumentId={dokumentId}
                        isPreview={isPreview}
                    />
                );
            case ResourceType.documentGenerator:
                return (
                    <DocumentGeneratorResource
                        resourceId={resourceId!}
                        isPreview={isPreview}
                    />
                );
            case ResourceType.pdf:
                return (
                    <PdfResource
                        resourceId={resourceId!}
                        onShown={pdfDocumentLoaded}
                    />
                );
            default:
                // The HTML variants and every other type, including legacy
                // ones the app has no name for. HTMLResource shows its own
                // error / empty status for a topic it can't render.
                return (
                    <HTMLResource
                        resourceId={resourceId!}
                        segmentId={segmentId}
                    />
                );
        }
    };

    const showFavorite =
        !isSkipped && !isPreview && !isError && resourceTypeId !== undefined;

    return (
        <PanelProvider>
            <AccessContext.Provider value={setAccessEntry}>
                {showFavorite && (
                    <TopBarSlot slot="right">
                        <FavoriteTopicButton topicId={resourceId!} />
                    </TopBarSlot>
                )}
                <div className={classes.contentPage}>
                    {/* One leaf instance per topic: keying on the topic id
                        remounts whichever resource component is rendered when
                        the user navigates between topics, so leaf-local state
                        (reducers, refs, form state) never survives a topic
                        switch. Intra-topic axes (segment, filling) stay owned
                        by the leaves. Falls back to a constant when there is
                        no resource id — that path renders a status message. */}
                    <Fragment key={resourceId ?? "no-resource"}>
                        {getContent()}
                    </Fragment>
                </div>
                <NoAccessModal opened={noAccess} />
                <SliderPanel />
            </AccessContext.Provider>
        </PanelProvider>
    );
};
